import { VisualEngineStats, VisualIdentityRun } from '@/modules/visual-engine/types'
import { NodeAxios } from '../api'

// Helper function to safely extract array data from API responses
const extractArrayData = (response: any): any[] => {
    void response
    const body = response.data !== undefined ? response.data : response
    // Handle both array response and {success: true, data: []} response
    if (Array.isArray(body)) {
        return body
    } else if (body.data && Array.isArray(body.data)) {
        return body.data
    }
    return []
}

// Helper function to safely extract object data with nested data field
const extractObjectData = (response: any, fallback: any = {}): any => {
    void response
    const body = response.data !== undefined ? response.data : response
    // Check if body.data exists and has the expected properties
    if (body.data && typeof body.data === 'object') {
        return body.data
    }
    // Check if body itself has the expected properties (already unwrapped)
    if (body && typeof body === 'object' && !body.data) {
        return body
    }
    return fallback
}

// ============================================
// PHASE 2 TYPE DEFINITIONS
// ============================================

export interface VisualValue {
    primary: Array<{ value: string; symbolicKeywords: string[] }>
    secondary: Array<{ value: string; symbolicKeywords: string[] }>
    opposingPairs: Array<{
        valueA: string
        valueB: string
        whyOpposing: string
        signalsA: string[]
        signalsB: string[]
        frequencyTarget: string
        /** From Generate Combined Beacon: why this strategy works */
        frequencyExplanation?: string
        /** From Generate Combined Beacon: How It Works Visually - the tactical rule for designers */
        frequencyVisualExample?: string
    }>
    primaryKeywords?: Record<string, Record<string, number>>
    aiSuggested: boolean
    userEdited: boolean
    locked: boolean
    generatedAt?: string
}

export interface CurrentDictionaryEntry {
    id: string
    sourceId?: string
    sourceType?: string
    symbolicCategory: string
    observedBehavior: string
    inferredSymbolicMeaning: string
    recurrenceLevel: 'isolated' | 'repeating' | 'dominant'
    mutabilityStatus: 'immutable' | 'mutable' | 'deferred'
    confidenceScore: number
    evidenceRefs: string[]
}

export interface AspirationalDictionaryEntry {
    id: string
    symbolicCategory: string
    requiredBehavior: string
    intendedMeaning: string
    supportedBy: {
        goals: string[]
        tfd: 'think' | 'feel' | 'do'
        alo: 'asset' | 'liability' | 'omission'
    }
    priorityLevel: 'critical' | 'important' | 'supporting'
    motifCompatibility: string[]
    confidenceScore: number
}

export interface VisualParameter {
    id: string
    displayName: string
    category: 'composition' | 'color' | 'form' | 'progression' | 'other'
    description: string
    controlType: 'slider' | 'options' | 'scale' | 'toggle'
    scale: {
        type: 'continuous' | 'discrete'
        min?: number
        max?: number
        step?: number
        options?: string[]
        labels?: Record<string, string>
    }
    phdRecommended: number | string
    userValue: number | string
    reasoning: {
        why: string
        impact: string
        sources: {
            values?: string[]
            motifs?: string[]
            tfdGoal?: {
                type: 'think' | 'feel' | 'do'
                text: string
            }
        }
    }
    visualExamples?: {
        low?: string
        medium?: string
        high?: string
    }
}

export interface StrategicSummary {
    overallApproach: string
    keyThemes: string[]
    persuasionMechanism: string
}

export interface GapResolutionEntry {
    symbolicCategory: string
    currentBehavior?: string
    aspirationalBehavior?: string
    resolution: 'carry_forward' | 'modify' | 'suppress' | 'introduce'
    rationale: string
    enforcementLevel: 'hard' | 'default' | 'optional'
}

export interface MotifFamily {
    id: string
    name: string
    description: string
    symbolicStructure: {
        orientation: 'vertical' | 'horizontal' | 'radial' | 'layered'
        openness: 'open' | 'contained' | 'bounded'
        complexity: 'simple' | 'moderate' | 'complex'
        organization: 'ordered' | 'progressive' | 'emergent'
    }
    behavioralCompatibility: {
        reinforces: string[]
        conflicts: string[]
    }
    semanticPayload: {
        primarySignals: string[]
        secondarySignals: string[]
    }
    forbids: {
        symbolicTypes: string[]
        spatialBehaviors: string[]
    }
    rationale: string
    compatibilityScore: number
    status: 'recommended' | 'compatible' | 'conflicting'
    selected?: boolean
}

export interface MotifSelectionResult {
    generatedFamilies: MotifFamily[]
    selectedFamilies: MotifFamily[]
    rejectedFamilies: MotifFamily[]
    overrides?: Array<{ familyId: string; justification: string }>
    locked: boolean
    generatedAt?: string
}

export const visualEngineService = {
    /**
     * List all visual identity runs
     */
    async listRuns(filters?: { status?: string; type?: string; summary?: boolean; tools?: boolean }): Promise<VisualIdentityRun[]> {
        const params = new URLSearchParams()
        if (filters?.status) params.append('status', filters.status)
        if (filters?.type) params.append('type', filters.type)
        if (filters?.summary) params.append('summary', 'true')
        if (filters?.tools) params.append('tools', 'true')

        return await NodeAxios.get(`/visual-engine/runs?${params.toString()}`)
    },

    /**
     * Get a specific visual identity run
     */
    async getRun(id: string): Promise<VisualIdentityRun> {
        return await NodeAxios.get(`/visual-engine/runs/${id}`)
    },

    async getRunSummary(id: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${id}/summary`)
        return extractObjectData(response, {})
    },

    /**
     * Get dependency status for a run (staleness indicators)
     */
    async getDependencyStatus(id: string): Promise<{
        runId: string
        statuses: Record<
            string,
            {
                phase: string
                screen: string
                status: 'fresh' | 'stale' | 'empty' | 'invalid'
                reason?: string
                upstreamChanges?: Array<{
                    source: string
                    changedAt: Date
                    impact: 'high' | 'medium' | 'low'
                }>
                canContinue: boolean
                needsRegeneration: boolean
            }
        >
        timestamp: Date
    }> {
        return await NodeAxios.get(`/visual-engine/runs/${id}/dependency-status`)
    },

    async getCampaignTopics(campaignId: string): Promise<any[]> {
        console.log('[visualEngineService] Fetching campaign topics for:', campaignId)
        try {
            return await NodeAxios.get(`/campaigns/${campaignId}/topics`)
        } catch (error: any) {
            console.error('[visualEngineService] Failed to fetch campaign topics:', error)
            // Return empty array if campaign topics unavailable
            return []
        }
    },

    /**
     * Get targets for a campaign or run
     */
    async getTargets(id: string, type: 'campaign' | 'run' = 'campaign'): Promise<any[]> {
        const endpoint = type === 'run' ? `/visual-engine/runs/${id}/targets` : `/campaigns/${id}/targets`
        const response: any = await NodeAxios.get(endpoint)
        return extractArrayData(response)
    },

    /**
     * Create a new visual identity run
     */
    async createRun(data: Partial<VisualIdentityRun>): Promise<VisualIdentityRun> {
        return await NodeAxios.post('/visual-engine/runs', data)
    },

    /**
     * Update an existing visual identity run
     */
    async updateRun(id: string, data: Partial<VisualIdentityRun>): Promise<VisualIdentityRun> {
        return await NodeAxios.patch(`/visual-engine/runs/${id}`, data)
    },

    /**
     * Import Phase 1 data (goals, target, TFD, ALOs) from a Persuasion Lab Foundations profile.
     * Appends records to this run; data structures match so it works across 1.1–1.4.
     */
    async importFromFoundation(
        runId: string,
        foundationRunId: string
    ): Promise<{ goalsAppended: number; targetAppended: boolean; alosAppended: number; message: string }> {
        const res: any = await NodeAxios.post(`/visual-engine/runs/${runId}/import-from-foundation`, {
            foundation_run_id: foundationRunId,
        })
        return res
    },

    /**
     * Delete a visual identity run
     */
    async deleteRun(id: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/visual-engine/runs/${id}`)
    },

    /**
     * Get engine statistics
     */
    async getStats(): Promise<VisualEngineStats> {
        return await NodeAxios.get('/visual-engine/stats')
    },

    /**
     * Trigger deep visual extraction of atomic artifacts from a URL
     */
    async extractArtifacts(
        runId: string,
        url: string,
        label?: string,
        type?: string,
        targets?: string[],
        forceReAnalysis?: boolean
    ): Promise<{ success: boolean; count: number }> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/extract`, {
            url,
            label,
            type,
            targets,
            forceReAnalysis,
        })
    },

    /**
     * Analyze an uploaded image artifact
     */
    /**
     * Add evidence/artifact by URL
     */
    async addEvidenceByUrl(runId: string, url: string, type: string, label: string): Promise<any> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/extract`, {
            url,
            type,
            label,
        })
    },

    async analyzeUpload(runId: string, imageUrl: string, label?: string): Promise<any> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/analyze-upload`, { imageUrl, label })
    },

    /**
     * Run PhD Analysis for a specific phase
     */
    async runAnalysis(runId: string, phase: string): Promise<any> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/analyze`, { phase })
    },

    /**
     * List visual artifacts for a specific run
     */
    async listArtifacts(runId: string, sourceUrl?: string, type?: string): Promise<any[]> {
        const params = new URLSearchParams()
        if (sourceUrl) params.append('sourceUrl', sourceUrl)
        if (type) params.append('type', type)

        const res = await NodeAxios.get(`/visual-engine/runs/${runId}/artifacts?${params.toString()}`)
        return res.data?.data || res.data || []
    },

    /**
     * Update a specific visual artifact
     */
    async updateArtifact(runId: string, artifactId: string, data: any): Promise<any> {
        const res = await NodeAxios.patch(`/visual-engine/runs/${runId}/artifacts/${artifactId}`, data)
        return res.data
    },

    /**
     * Sync artifact assignments for a specific source URL
     */
    async syncArtifactAssignments(runId: string, sourceUrl: string, assignedTo: string[]): Promise<any> {
        const res = await NodeAxios.post(`/visual-engine/runs/${runId}/artifacts/sync-assignments`, {
            sourceUrl,
            assignedTo,
        })
        return res.data
    },

    /**
     * Delete all artifacts for a specific source URL
     */
    async deleteArtifactsBySource(
        runId: string,
        sourceUrl: string
    ): Promise<{ success: boolean; deletedCount: number }> {
        const res = await NodeAxios.post(`/visual-engine/runs/${runId}/artifacts/delete-by-source`, { sourceUrl })
        return res.data
    },

    /**
     * Delete a specific artifact
     */
    async deleteArtifact(runId: string, artifactId: string): Promise<{ success: boolean }> {
        const res = await NodeAxios.delete(`/visual-engine/runs/${runId}/artifacts/${artifactId}`)
        return res.data
    },

    /**
     * Toggle an artifact as primary (canonical) for its layer type
     */
    async toggleArtifactPrimary(runId: string, artifactId: string): Promise<any> {
        const res = await NodeAxios.post(`/visual-engine/runs/${runId}/artifacts/${artifactId}/toggle-primary`)
        return res.data
    },

    // PhD Prompts (Admin)
    async listPrompts(): Promise<any[]> {
        const response: any = await NodeAxios.get('/visual-engine/prompts')
        return extractArrayData(response)
    },

    async createPrompt(data: any): Promise<any> {
        return await NodeAxios.post('/visual-engine/prompts', data)
    },

    async upsertPrompt(data: any): Promise<any> {
        return await NodeAxios.put('/visual-engine/prompts/upsert', data)
    },

    async updatePrompt(id: string, data: any): Promise<any> {
        return await NodeAxios.patch(`/visual-engine/prompts/${id}`, data)
    },

    async deletePrompt(id: string): Promise<any> {
        return await NodeAxios.delete(`/visual-engine/prompts/${id}`)
    },

    async seedPrompts(): Promise<any> {
        return await NodeAxios.post('/visual-engine/prompts/seed')
    },

    // ============================================
    // PHASE 2.1: VISUAL VALUES
    // ============================================

    /**
     * Generate visual values from goals (2.1)
     * POST /api/visual-engine/runs/:id/phase2/visual-values/generate
     */
    async generateVisualValues(runId: string, options: { force?: boolean } = {}): Promise<any> {
        const response = await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/visual-values/generate`, options)
        return response.data !== undefined ? response.data : response
    },

    /**
     * Get visual values (2.1)
     * GET /api/visual-engine/runs/:id/phase2/visual-values
     */
    async getVisualValues(runId: string): Promise<VisualValue | null> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase2/visual-values`)
        const data = response.data !== undefined ? response.data : response
        return data as VisualValue
    },

    /**
     * Update visual values (2.1)
     * PATCH /api/visual-engine/runs/:id/phase2/visual-values
     */
    async updateVisualValues(runId: string, values: Partial<VisualValue>): Promise<VisualValue> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase2/visual-values`, values)
        const data = response.data !== undefined ? response.data : response
        return data as VisualValue
    },

    /**
     * Lock visual values (2.1)
     * POST /api/visual-engine/runs/:id/phase2/visual-values/lock
     */
    async lockVisualValues(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/visual-values/lock`)
    },

    // ============================================
    // PHASE 2.2: MOTIF SELECTION
    // ============================================

    /**
     * Generate motif families (2.2)
     * POST /api/visual-engine/runs/:id/phase2/motif-selection/generate
     */
    async generateMotifFamilies(runId: string): Promise<{ success: boolean; jobId: string }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/motif-selection/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || body
    },

    /**
     * Save motif selection (2.2)
     * POST /api/visual-engine/runs/:id/phase2/motif-selection/save
     */
    async saveMotifSelection(
        runId: string,
        selection: { selectedIds: string[]; rejectedIds: string[]; locked?: boolean; allFamilies?: MotifFamily[] }
    ): Promise<MotifSelectionResult> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase2/motif-selection/save`,
            selection
        )
        return response.data !== undefined ? response.data : response
    },

    // ============================================
    // PHASE 2.3: CURRENT SYMBOLIC DICTIONARY
    // ============================================

    /**
     * Get current dictionary (2.3)
     */
    async getCurrentDictionary(runId: string): Promise<{ entries: CurrentDictionaryEntry[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase2/current-dictionary`)
        return extractObjectData(response, { entries: [], locked: false })
    },

    /**
     * Analyze artifacts to extract dictionary (2.3)
     */
    async analyzeCurrentDictionary(runId: string): Promise<CurrentDictionaryEntry[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/current-dictionary/analyze`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock current dictionary (2.3)
     */
    async lockCurrentDictionary(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/current-dictionary/lock`)
    },

    /**
     * Update current dictionary (2.3)
     */
    async updateCurrentDictionary(runId: string, entries: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase2/current-dictionary`, {
            entries,
        })
        return extractArrayData(response)
    },

    // ============================================
    // PHASE 2.4: ASPIRATIONAL SYMBOLIC DICTIONARY
    // ============================================

    /**
     * Get aspirational dictionary (2.4)
     */
    async getAspirationalDictionary(runId: string): Promise<{ entries: any[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase2/aspirational-dictionary`)
        return extractObjectData(response, { entries: [], locked: false })
    },

    /**
     * Generate aspirational dictionary (2.4)
     */
    async generateAspirationalDictionary(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase2/aspirational-dictionary/generate`
        )
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock aspirational dictionary (2.4)
     */
    async lockAspirationalDictionary(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/aspirational-dictionary/lock`)
    },

    /**
     * Get visual parameters (2.4 Parameter Control)
     * @param runId - Run ID
     * @param includeAudit - If true, include generationAudit (context, per-param source, parse errors)
     */
    async getVisualParameters(
        runId: string,
        includeAudit = false
    ): Promise<{
        parameters: VisualParameter[]
        locked: boolean
        strategicSummary?: StrategicSummary
        generationAudit?: any
    }> {
        const url = includeAudit
            ? `/visual-engine/runs/${runId}/phase2/visual-parameters?debug=1`
            : `/visual-engine/runs/${runId}/phase2/visual-parameters`
        const response: any = await NodeAxios.get(url)
        return extractObjectData(response, { parameters: [], locked: false, strategicSummary: undefined })
    },

    /**
     * Generate visual parameters (2.4 Parameter Control)
     * @param force - If true, regenerate even if parameters already exist
     */
    async generateVisualParameters(runId: string, force: boolean = false): Promise<VisualParameter[]> {
        console.log(`[Service] generateVisualParameters called with force=${force}`)
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/visual-parameters/generate`, {
            force,
        })
        const body = response.data !== undefined ? response.data : response

        // New: Job is queued asynchronously, socket will update when ready
        // Return empty array so component knows to wait for socket event
        console.log(`[Service] Parameter generation job queued:`, body.jobId || 'unknown')
        return []
    },

    /**
     * Update visual parameters (2.4 Parameter Control)
     */
    async updateVisualParameters(runId: string, parameters: VisualParameter[]): Promise<void> {
        await NodeAxios.patch(`/visual-engine/runs/${runId}/phase2/visual-parameters`, { parameters })
    },

    /**
     * Lock visual parameters (2.4 Parameter Control)
     */
    async lockVisualParameters(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/visual-parameters/lock`)
    },

    /**
     * Update aspirational dictionary (2.4)
     */
    async updateAspirationalDictionary(runId: string, entries: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase2/aspirational-dictionary`, {
            entries,
        })
        return extractArrayData(response)
    },

    // ============================================
    // PHASE 2.5: GAP RESOLUTION
    // ============================================

    /**
     * Get gap resolution (2.5)
     */
    async getGapResolution(runId: string): Promise<{ resolutions: GapResolutionEntry[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase2/gap-resolution`)
        return extractObjectData(response, { resolutions: [], locked: false })
    },

    /**
     * Generate gap resolution (2.5)
     */
    async generateGapResolution(runId: string): Promise<GapResolutionEntry[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/gap-resolution/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock gap resolution (2.5)
     */
    async lockGapResolution(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/gap-resolution/lock`)
    },

    /**
     * Update gap resolution (2.5)
     */
    async updateGapResolution(runId: string, resolutions: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase2/gap-resolution`, {
            resolutions,
        })
        return extractArrayData(response)
    },

    /**
     * Lock entire Phase 2 (Visual Identity System)
     * Locks all Phase 2 components: values, motifs, parameters, and creative brief
     */
    async lockPhase2(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase2/lock`)
    },

    // ============================================
    // PHASE 3.1: VISUAL FOUNDATION
    // ============================================

    /**
     * Get visual foundation (3.1)
     */
    async getVisualFoundation(runId: string): Promise<{ rules: any[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/foundation`)
        return extractObjectData(response, { rules: [], locked: false })
    },

    /**
     * Generate visual foundation (3.1)
     */
    async generateVisualFoundation(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/foundation/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock visual foundation (3.1)
     */
    async lockVisualFoundation(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/foundation/lock`)
    },

    /**
     * Update visual foundation (3.1)
     */
    async updateVisualFoundation(runId: string, rules: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/foundation`, { rules })
        return extractArrayData(response)
    },

    /**
     * Get creative direction principles (3.1 NEW)
     */
    async getCreativeDirection(runId: string): Promise<{ principles: any[] }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/creative-direction`)
        return extractObjectData(response, { principles: [] })
    },

    /**
     * Generate creative direction principles with AI (3.1 NEW)
     */
    async generateCreativeDirection(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/creative-direction/generate`)
    },

    /**
     * Update creative direction principles (3.1 NEW)
     */
    async updateCreativeDirection(runId: string, principles: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/creative-direction`, {
            principles,
        })
        return extractArrayData(response)
    },

    // ============================================
    // PHASE 3.2: CHARACTER CAST
    // ============================================

    /**
     * Get character cast (3.2)
     */
    async getCharacterCast(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/characters`)
        return extractObjectData(response, {
            characters: [],
            presence: 'Limited',
            relationshipToViewer: 'Participant',
            behavioralPosture: 'Exploratory',
            avoidRules: {
                noConferenceTables: true,
                noExaggeratedSmiles: true,
                noStockPhotoPoses: true,
                noStagedHandshakes: true,
                noPerformativePointing: true,
                noStartupTeamCliche: true,
            },
            locked: false,
        })
    },

    /**
     * Generate character cast (3.2)
     */
    async generateCharacterCast(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase3/characters/generate`,
            {},
            { timeout: 45000 }
        )
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock character cast (3.2)
     */
    async lockCharacterCast(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/characters/lock`)
    },

    /**
     * Update character cast (3.2)
     */
    async updateCharacterCast(runId: string, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/characters`, data)
        return extractObjectData(response, {})
    },

    /**
     * Suggest energy tags for a character based on their role and context (3.2)
     */
    async suggestCharacterEnergyTags(runId: string, context: any): Promise<any> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase3/characters/suggest-tags`,
            context
        )
        return extractObjectData(response, { suggestions: '' })
    },

    // ============================================
    // PHASE 3.3: SCENE & ENVIRONMENT
    // ============================================

    /**
     * Get scene environment (3.3)
     */
    async getSceneEnvironment(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/environments`)
        return extractObjectData(response, {
            environments: [],
            abstractionLevel: 'Contextual',
            spatialRelationship: 'Navigable',
            environmentRole: 'Context',
            temporalSignal: 'Timeless',
            avoidRules: {
                noConferenceRooms: true,
                noPresentationStages: true,
                noLiteralOfficeWork: true,
                noAbstractVoid: true,
                noFantasyArchitecture: true,
            },
            locked: false,
        })
    },

    /**
     * Generate scene environment (3.3)
     */
    async generateSceneEnvironment(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/environments/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Generate single environment for specific usage context (3.3)
     */
    async generateSingleEnvironment(runId: string, usageContext: string, userContext?: string): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/environments/generate-single`, {
            usageContext,
            ...(userContext ? { userContext } : {}),
        })
        const body = response.data !== undefined ? response.data : response
        return body.data || {}
    },

    /**
     * Lock scene environment (3.3)
     */
    async lockSceneEnvironment(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/environments/lock`)
    },

    /**
     * Update scene environment (3.3)
     */
    async updateSceneEnvironment(runId: string, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/environments`, data)
        return extractObjectData(response, {})
    },

    // ============================================
    // PHASE 3.4: CAMERA & MOTION
    // ============================================

    /**
     * Get camera & motion (3.4)
     */
    async getCameraMotion(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/camera-motion`)
        return extractObjectData(response, {
            rules: [],
            momentSelection: 'Mid-Action',
            subjectAwareness: 'Unaware',
            motionCause: 'None',
            customConstraints: [],
            avoidRules: {},
            locked: false,
        })
    },

    /**
     * Generate camera & motion (3.4)
     */
    async generateCameraMotion(runId: string, force: boolean = false): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/camera-motion/generate`, {
            force,
        })
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock camera & motion (3.4)
     */
    async lockCameraMotion(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/camera-motion/lock`)
    },

    /**
     * Update camera & motion (3.4)
     */
    async updateCameraMotion(runId: string, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/camera-motion`, data)
        return extractObjectData(response, {
            rules: [],
            momentSelection: 'Mid-Action',
            subjectAwareness: 'Unaware',
            motionCause: 'None',
            customConstraints: [],
            avoidRules: {},
            locked: false,
        })
    },

    // ============================================
    // PHASE 3.4.1: COLOR THEORY
    // ============================================

    /**
     * Get color theory (3.4.1)
     */
    async getColorTheory(runId: string): Promise<{ surfaceRoles: any; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/color-theory`)
        return extractObjectData(response, { surfaceRoles: null, locked: false })
    },

    /**
     * Generate color theory (3.4.1)
     */
    async generateColorTheory(runId: string): Promise<{ success: boolean; jobId: string }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/color-theory/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || body
    },

    /**
     * Lock color theory (3.4.1)
     */
    async lockColorTheory(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/color-theory/lock`)
    },

    /**
     * Update color theory (3.4.1)
     */
    async updateColorTheory(runId: string, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/color-theory`, data)
        return response.data !== undefined ? response.data : response
    },

    // ============================================
    // PHASE 3.5: MATERIAL & TEXTURE
    // ============================================

    /**
     * Get material & texture (3.5)
     */
    async getMaterialTexture(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/material-texture`)
        return extractObjectData(response, {
            materials: [],
            surfaceEnergy: 'Smooth',
            materialBias: 'Refined',
            agingSignal: 'Pristine',
            consistencyBehavior: 'Uniform',
            avoidRules: {
                noToyPlasticSheen: true,
                noOverGlossReflections: true,
                noMicrotextureOverdetail: true,
                noGrungeOverlay: true,
                noCgiRenderSurfaces: true,
                noChromeDominance: true,
            },
            locked: false,
        })
    },

    /**
     * Generate material & texture (3.5)
     */
    async generateMaterialTexture(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/material-texture/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Lock material & texture (3.5)
     */
    async lockMaterialTexture(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/material-texture/lock`)
    },

    /**
     * Update material & texture (3.5)
     */
    async updateMaterialTexture(runId: string, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase3/material-texture`, data)
        return extractObjectData(response, {})
    },

    // ============================================
    // PHASE 3.6: DIRECTION LOCK (STARTER IMAGES)
    // ============================================

    /**
     * Get starter images (3.6)
     */
    async getStarterImages(runId: string): Promise<{ starterImages: any[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase3/starter-images`)
        return extractObjectData(response, { starterImages: [], locked: false })
    },

    /**
     * Generate starter images (3.6)
     */
    async generateStarterImages(runId: string, adjustments?: any): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/starter-images/generate`, {
            adjustments,
        })
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Trigger a manual aesthetic audit for a starter image
     */
    async triggerAestheticAudit(runId: string, imageIndex: number): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/starter-images/${imageIndex}/audit`)
    },

    async previewStarterImagePrompt(
        runId: string,
        profileName: string
    ): Promise<{ prompt: string; phdData: any; audit: any }> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase3/starter-images/preview-prompt`,
            { profileName }
        )
        return extractObjectData(response)
    },

    /**
     * 🔥 NEW: Batch preview for multiple profiles via RabbitMQ
     */
    async previewStarterImagePromptBatch(runId: string, profiles: string[]): Promise<{ jobs: any[]; message: string }> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase3/starter-images/preview-prompt`,
            { profiles }
        )
        return extractObjectData(response)
    },

    /**
     * 🔥 NEW: Get prompt compiler job status
     */
    async getPromptCompilerJobStatus(
        runId: string
    ): Promise<{ processing: boolean; totalMessages: number; queues: any[] }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/debug/compiler-status`)
        return extractObjectData(response)
    },

    /**
     * Lock visual direction (3.6)
     */
    async lockVisualDirection(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/lock`)
    },

    /**
     * Approve a starter image (3.6)
     */
    async approveStarterImage(runId: string, imageIndex: number): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase3/starter-images/${imageIndex}/approve`)
    },

    /**
     * Update a specific starter image (e.g. evaluation, validated status)
     */
    async updateStarterImage(runId: string, imageIndex: number, data: any): Promise<any> {
        const response: any = await NodeAxios.patch(
            `/visual-engine/runs/${runId}/phase3/starter-images/${imageIndex}`,
            data
        )
        return response.data !== undefined ? response.data : response
    },

    async deleteStarterImage(runId: string, imageIndex: number, profile?: string): Promise<void> {
        const qs = profile ? `?profile=${encodeURIComponent(profile)}` : ''
        await NodeAxios.delete(`/visual-engine/runs/${runId}/phase3/starter-images/${imageIndex}${qs}`)
    },

    /**
     * Lock direction (alias for lockVisualDirection)
     */
    async lockDirection(runId: string): Promise<void> {
        return this.lockVisualDirection(runId)
    },

    // ============================================
    // Phase 4: Visual Application
    // ============================================

    // 4.1: Situation Selection
    async getSituations(runId: string): Promise<{ situations: any[]; locked: boolean }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/situations`)
        return extractObjectData(response, { situations: [], locked: false })
    },

    async generateSituations(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/situations/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async updateSituations(runId: string, situations: any[]): Promise<any[]> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/phase4/situations`, { situations })
        return extractArrayData(response)
    },

    async lockSituations(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/situations/lock`)
    },

    // 4.2: Three Objects Studio
    async getStudio(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/studio`)
        // Response structure: { success: true, data: [...setups] }
        // So response.data is already the array we want
        const result = Array.isArray(response.data) ? response.data : response.data?.data || []
        console.log('[visualEngineService] getStudio returning:', result.length, 'setups')
        return result
    },

    async generateStudioSetup(runId: string, situationId: string): Promise<any> {
        try {
            console.log('[visualEngineService] generateStudioSetup called:', { runId, situationId })
            const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/setup`, {
                situationId,
            })
            console.log('[visualEngineService] generateStudioSetup raw response:', response)
            console.log('[visualEngineService] generateStudioSetup response.data:', response.data)

            const body = response.data !== undefined ? response.data : response
            console.log('[visualEngineService] generateStudioSetup body:', body)
            console.log('[visualEngineService] generateStudioSetup body.data:', body.data)

            const result = extractObjectData(response, null)
            console.log('[visualEngineService] generateStudioSetup extracted result:', result)

            return result
        } catch (error: any) {
            console.error('[visualEngineService] generateStudioSetup ERROR:', error)
            console.error('[visualEngineService] generateStudioSetup error.response:', error.response)
            throw error
        }
    },

    async generateStudioAsset(runId: string, setupId: string): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/${setupId}/generate`)
        const body = response.data !== undefined ? response.data : response
        return body.data
    },

    async approveStudioOutput(runId: string, setupId: string, outputIndex: number, approved: boolean): Promise<void> {
        console.log('[visualEngineService] Approving studio output:', { runId, setupId, outputIndex, approved })
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/${setupId}/outputs/${outputIndex}/approve`, {
            approved,
        })
    },

    async getQualityControls(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/quality-controls`)
        return response.data
    },

    async saveQualityControls(runId: string, controls: any): Promise<void> {
        console.log('[visualEngineService] Saving quality controls:', { runId, controls })
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/quality-controls`, controls)
    },

    async refreshBrandColors(runId: string, currentControls: any): Promise<{ success: boolean; colors: string[] }> {
        console.log('[visualEngineService] Refreshing brand colors:', { runId, currentControls })
        try {
            const response: any = await NodeAxios.post(
                `/visual-engine/runs/${runId}/phase4/quality-controls/refresh-colors`,
                currentControls
            )
            console.log('[visualEngineService] Refresh response:', response)
            console.log('[visualEngineService] Response type:', typeof response)
            // NodeAxios returns data directly, not in response.data
            return response as { success: boolean; colors: string[] }
        } catch (error: any) {
            console.error('[visualEngineService] Error in refreshBrandColors:', error)
            console.error('[visualEngineService] Error response:', error.response)
            throw error
        }
    },

    async getPhdControls(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/phd-controls`)
        return response.data
    },

    async savePhdControls(runId: string, controls: any): Promise<void> {
        console.log('[visualEngineService] Saving PhD controls:', { runId, controls })
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/phd-controls`, controls)
    },

    async generateQuickTopic(runId: string, setupId: string, topic: string, situationName: string): Promise<any> {
        console.log('[visualEngineService] Generating quick topic:', { runId, setupId, topic, situationName })
        try {
            const response: any = await NodeAxios.post(
                `/visual-engine/runs/${runId}/phase4/studio/${setupId}/quick-topic`,
                {
                    topic,
                    situationName,
                }
            )
            console.log('[visualEngineService] Quick topic response:', response)
            console.log('[visualEngineService] Response type:', typeof response)
            // NodeAxios returns data directly, not in response.data
            return response
        } catch (error: any) {
            console.error('[visualEngineService] Quick topic error:', error)
            console.error('[visualEngineService] Error response:', error?.response)
            console.error('[visualEngineService] Error response data:', error?.response?.data)
            throw error
        }
    },

    async lockStudio(runId: string): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/lock`)
    },

    async refineStudioOutput(runId: string, setupId: string, outputIndex: number, instructions: string): Promise<void> {
        console.log('[visualEngineService] Refining output with instructions:', {
            runId,
            setupId,
            outputIndex,
            instructions,
        })
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/${setupId}/refine/${outputIndex}`, {
            instructions,
        })
    },

    async setAsReferenceImage(runId: string, setupId: string, outputIndex: number): Promise<void> {
        console.log('[visualEngineService] Setting output as reference image:', { runId, setupId, outputIndex })
        await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/studio/${setupId}/set-reference/${outputIndex}`)
    },

    async deleteStudioOutput(runId: string, setupId: string, outputIndex: number): Promise<void> {
        console.log('[visualEngineService] Deleting studio output:', { runId, setupId, outputIndex })
        await NodeAxios.delete(`/visual-engine/runs/${runId}/phase4/studio/${setupId}/outputs/${outputIndex}`)
    },

    // 4.3: Campaign Stress Test
    async getStressTest(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/stress-test`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async getStressTestStatus(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/stress-test/status`)
        const body = response.data !== undefined ? response.data : response
        return body.status || { status: 'idle' }
    },

    async runStressTest(runId: string, topics: any[], boundaryTests?: any): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/stress-test/run`, {
            topics,
            boundaryTests,
        })
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async submitStressTestFeedback(runId: string, resultIndex: number, feedback: any): Promise<any> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase4/stress-test/${resultIndex}/feedback`,
            feedback
        )
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async regenerateStressTestImage(runId: string, resultIndex: number, adjustments: string): Promise<any> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase4/stress-test/${resultIndex}/regenerate`,
            { adjustments }
        )
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async getVerbotenList(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/verboten`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async addToVerbotenList(
        runId: string,
        item: { term: string; description?: string; source?: string }
    ): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/verboten`, item)
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async removeFromVerbotenList(runId: string, term: string): Promise<any> {
        const response: any = await NodeAxios.delete(`/visual-engine/runs/${runId}/phase4/verboten`, {
            data: { term },
        })
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async saveStressTestSelections(
        runId: string,
        selections: {
            selectedCampaignId?: string
            selectedTopicIds?: string[]
            boundaryTests?: any
        }
    ): Promise<any> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase4/stress-test/selections`,
            selections
        )
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async getStressTestSelections(runId: string): Promise<any> {
        try {
            console.log('[visualEngineService] 📞 GET /visual-engine/runs/${runId}/phase4/stress-test/selections')
            const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/stress-test/selections`)
            console.log('[visualEngineService] 📦 Response:', response)
            const body = response.data !== undefined ? response.data : response
            console.log('[visualEngineService] 📄 Body:', body)
            // Backend returns { success: true, data: selections }
            // So body.data has the selections, but if body is already selections, return body
            const result = body.data || body || {}
            console.log('[visualEngineService] 📤 Returning:', result)
            return result
        } catch (error) {
            console.error('[visualEngineService] ❌ Error getting selections:', error)
            return {}
        }
    },

    // 3-Stage Stress Test Workflow
    async generateStressTestPrompts(runId: string, topics: any[]): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/stress-test/prompts/generate`, {
            topics,
        })
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async getStressTestPrompts(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/stress-test/prompts`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async updateStressTestPrompt(runId: string, promptIndex: number, editedPrompt: string): Promise<any> {
        const response: any = await NodeAxios.patch(
            `/visual-engine/runs/${runId}/phase4/stress-test/prompts/${promptIndex}`,
            { editedPrompt }
        )
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async approveStressTestPrompts(runId: string, promptIndices: number[]): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/stress-test/prompts/approve`, {
            promptIndices,
        })
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async generateStressTestImages(runId: string): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/stress-test/images/generate`)
        const body = response.data !== undefined ? response.data : response
        return body
    },

    async clearStressTestStatus(runId: string): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/stress-test/clear-status`)
        const body = response.data !== undefined ? response.data : response
        return body
    },

    // Real-Time Prompt Generation (RabbitMQ-based)
    async queuePromptGeneration(runId: string, content: any): Promise<any> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/queue-prompt`, content)
    },

    // Real-Time Image Generation (uses already-generated prompts)
    async queueImageGeneration(runId: string, content: any): Promise<any> {
        return await NodeAxios.post(`/visual-engine/runs/${runId}/queue-image`, content)
    },

    // 4.4: Lock & Deploy
    async getDeployment(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase4/deploy`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async lockAndDeploy(
        runId: string,
        deployment: { scope: 'campaign' | 'global'; selectedSetupIds: string[] }
    ): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase4/deploy/lock`, deployment)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    // ============================================
    // Phase 5: Visual Library
    // ============================================

    // 5.1: Prompt Library
    async getLibraryPrompts(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase5/prompts`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async syncLibrary(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase5/prompts/sync`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    // 5.2: Asset Gallery
    async getAssetGallery(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase5/assets`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async addAssetToGallery(runId: string, asset: any): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase5/assets/add`, asset)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    // 5.3: Feedback Loop
    async getFeedback(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/phase5/feedback`)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    async recordFeedback(runId: string, feedback: any): Promise<any[]> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/phase5/feedback/add`, feedback)
        const body = response.data !== undefined ? response.data : response
        return body.data || []
    },

    /**
     * Chat with AI for visual research (2.1)
     * POST /api/visual-engine/runs/:id/phase2/visual-values/chat-research
     * @deprecated Use universal chatVisualResearch instead
     */
    async chatVisualResearchLegacy(
        runId: string,
        params: {
            conversation: Array<{ role: 'user' | 'assistant'; content: string }>
            goals: any[]
            existingNodes: any[]
        }
    ): Promise<{ success: boolean; message: string; extractedNodes: any[] }> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase2/visual-values/chat-research`,
            params
        )
        const body = response.data !== undefined ? response.data : response
        return {
            success: body.success ?? false,
            message: body.message ?? "I've analyzed your visual intent.",
            extractedNodes: body.extractedNodes ?? [],
        }
    },

    /**
     * Expand manifestations for a visual value (2.1)
     * POST /api/visual-engine/runs/:id/phase2/visual-values/expand-manifestations
     */
    async expandManifestations(
        runId: string,
        params: {
            value: string
            weight: string
            existingManifestations: string[]
            opposingValue?: string
            beacon?: string
        }
    ): Promise<string[]> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase2/visual-values/expand-manifestations`,
            params
        )
        const body = response.data !== undefined ? response.data : response
        return body.manifestations || []
    },

    /**
     * Generate beacon options for visual values (2.1)
     * POST /api/visual-engine/runs/:id/phase2/visual-values/generate-beacon-options
     */
    async generateBeaconOptions(
        runId: string,
        params: {
            valueA: string
            valueB: string
            signalsA: string[]
            signalsB: string[]
            visualDirection?: string
        }
    ): Promise<Array<{ beacon: string; explanation: string; visualExample: string }>> {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/phase2/visual-values/generate-beacon-options`,
            params
        )
        const body = response.data !== undefined ? response.data : response
        return body.options || []
    },

    /**
     * Pre-populate next screen by triggering its generation
     * Fire-and-forget - don't wait for completion, just queue the job
     */
    async triggerNextScreenGeneration(runId: string, currentScreenId: string): Promise<void> {
        const screenToGenerateMap: Record<string, () => Promise<any>> = {
            // Phase 1 → Phase 2
            '1.1': () => this.generateVisualValues(runId),
            '1.4': () => this.generateVisualValues(runId),

            // Phase 2 flow
            '2.1': () => this.generateMotifFamilies(runId),
            '2.2': () => NodeAxios.post(`/visual-engine/runs/${runId}/phase2/current-dictionary/analyze`),
            '2.3': () => this.generateVisualParameters(runId), // Pre-generate parameters for 2.4
            '2.4': () => this.generateAspirationalDictionary(runId), // Pre-generate aspirational dict for prompt compiler
            '2.5': () => this.generateVisualFoundation(runId),

            // Phase 3 flow
            '3.1': () => this.generateCharacterCast(runId),
            '3.2': () => this.generateSceneEnvironment(runId),
            '3.3': () => this.generateCameraMotion(runId),
            '3.4': () => this.generateColorTheory(runId),
            '3.4.1': () => this.generateMaterialTexture(runId),
            '3.5': () => this.generateStarterImages(runId),

            // Phase 4 flow
            '3.6': () => this.generateSituations(runId),
            '4.1': () => Promise.resolve(), // Studio needs situationId, skip auto-trigger
        }

        const generateFn = screenToGenerateMap[currentScreenId]
        if (generateFn) {
            try {
                console.log(`[VisualEngine] Pre-populating next screen after ${currentScreenId}`)
                // Fire and forget - don't await
                generateFn().catch(err => {
                    console.warn(
                        `[VisualEngine] Pre-population failed for screen after ${currentScreenId}:`,
                        err.message
                    )
                })
            } catch (error) {
                console.warn(`[VisualEngine] Failed to trigger next screen generation:`, error)
            }
        }
    },

    // ============================================
    // UNIVERSAL RESEARCH SYSTEM
    // ============================================

    /**
     * Context-aware AI chat research (works for ALL screens)
     * POST /api/visual-engine/runs/:id/research/chat
     */
    async chatVisualResearch(
        runId: string,
        params: {
            context: string
            conversation: Array<{ role: 'user' | 'assistant'; content: string }>
            existingNodes?: any[]
        }
    ): Promise<{
        success: boolean
        message: string
        extractedNodes: any[]
        suggestions?: any
    }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/research/chat`, params)
        const body = response.data !== undefined ? response.data : response
        return {
            success: body.success ?? true,
            message: body.message || 'Analysis complete',
            extractedNodes: body.extractedNodes || [],
            suggestions: body.suggestions,
        }
    },

    /**
     * Beacon stress test — structured JSON for formatted display
     * POST /api/visual-engine/runs/:id/research/beacon-stress-test
     */
    async runBeaconStressTest(
        runId: string,
        params: {
            primary: string
            counter: string
            result: string
            visualDirection?: string
            iterationHistory?: Array<{
                primary: string
                counter: string
                result: string
                classification: string
                conceptualSharpness?: string
                polarityCheck?: string
            }>
        }
    ): Promise<{
        success: boolean
        conceptualSharpness?: string
        polarityCheck?: string
        visualRenderability?: string
        emotionalDepth?: string
        collapseTest?: string
        rewrittenEquation?: { primary: string; counter: string; result: string }
        alternativeTriads?: Array<{ primary: string; counter: string; result: string; rank: number }>
        classification?: string
        defense?: string
        problemType?: 'beacon' | 'values' | 'both'
        error?: string
    }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/research/beacon-stress-test`, params)
        const body = response.data !== undefined ? response.data : response
        return body
    },

    /**
     * Foundation tensions — raw identity arcs, ALOs, and goals for excavation panel
     * GET /api/visual-engine/runs/:id/research/foundation-tensions
     */
    async getFoundationTensions(runId: string): Promise<{
        success: boolean
        data: {
            identityArcs: Array<{
                targetName: string
                failure: { label: string; indicators?: string[] } | null
                transition: { label: string; indicators?: string[] } | null
                success: { label: string; indicators?: string[] } | null
                aspirations: string[]
                tfd: any
            }>
            alos: {
                assets: Array<{ type: string; text: string; description?: string }>
                liabilities: Array<{ type: string; text: string; description?: string }>
                omissions: Array<{ type: string; text: string; description?: string }>
            }
            goals: Array<{ name: string; description?: string }>
        }
    }> {
        // NodeAxios returns data directly (already unwrapped), so response IS the body
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/research/foundation-tensions`)
        return response
    },

    /**
     * Generate 6 bold visual declarations from a selected tension
     * POST /api/visual-engine/runs/:id/research/visual-declarations
     */
    async generateVisualDeclarations(
        runId: string,
        params: { tensionText: string; tensionType: string; visualDirection?: string }
    ): Promise<{
        success: boolean
        declarations: Array<{ beacon: string; vision: string; whyItWorks: string }>
    }> {
        // NodeAxios returns data directly (already unwrapped), so response IS the body
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/research/visual-declarations`, params)
        return response
    },

    /**
     * Save research findings back to screen data
     * POST /api/visual-engine/runs/:id/research/save
     */
    async saveResearchFindings(
        runId: string,
        params: {
            context: string
            findings: any
            updateType?: 'append' | 'replace'
        }
    ): Promise<{ success: boolean; updated: boolean }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/research/save`, params)
        const body = response.data !== undefined ? response.data : response
        return {
            success: body.success ?? true,
            updated: body.updated ?? false,
        }
    },

    /**
     * Get research config for a context
     * GET /api/visual-engine/runs/:id/research/context/:context
     */
    async getResearchConfig(runId: string, context: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/research/context/${context}`)
        const body = response.data !== undefined ? response.data : response
        return body.config || {}
    },

    // ============================================
    // NEW: API Control Panel & Asset Library
    // ============================================

    /**
     * Get API settings for campaign integration
     */
    async getAPISettings(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/api/settings`)
        return extractObjectData(response, {})
    },

    async getRunApiProfiles(runId: string): Promise<{ default_profile_id: string | null; profiles: any[] }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/api/profiles`)
        return extractObjectData(response, { default_profile_id: null, profiles: [] })
    },

    async createRunApiProfile(runId: string, payload: { name?: string; baseProfileId?: string } = {}) {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/api/profiles`, payload)
        return extractObjectData(response)
    },

    async updateRunApiProfile(runId: string, profileId: string, payload: any) {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/api/profiles/${profileId}`, payload)
        return extractObjectData(response)
    },

    async duplicateRunApiProfile(runId: string, profileId: string, payload: { name?: string } = {}) {
        const response: any = await NodeAxios.post(
            `/visual-engine/runs/${runId}/api/profiles/${profileId}/duplicate`,
            payload
        )
        return extractObjectData(response)
    },

    async setDefaultRunApiProfile(runId: string, profileId: string) {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/api/profiles/default`, { profileId })
        return extractObjectData(response)
    },

    async deleteRunApiProfile(runId: string, profileId: string) {
        const response: any = await NodeAxios.delete(`/visual-engine/runs/${runId}/api/profiles/${profileId}`)
        return extractObjectData(response)
    },

    /**
     * Save API settings
     */
    async saveAPISettings(runId: string, settings: any): Promise<void> {
        await NodeAxios.post(`/visual-engine/runs/${runId}/api/settings`, settings)
    },

    async setApiEnabled(runId: string, enabled: boolean): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/api/enabled`, { enabled })
        return extractObjectData(response)
    },

    async patchApiImageControls(runId: string, imageControls: Record<string, any>): Promise<any> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/api/image-controls`, { imageControls })
        return extractObjectData(response)
    },

    // ============================================
    // PHASE 4.2: CALIBRATION
    // ============================================
    async getCalibration(runId: string): Promise<{
        success: boolean
        run: { id: string; name: string }
        driftCalibration: any
    }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/calibration`)
        return extractObjectData(response, { success: false, run: { id: runId, name: '' }, driftCalibration: {} })
    },

    async patchCalibration(runId: string, patch: any): Promise<{
        success: boolean
        run: { id: string; name: string }
        driftCalibration: any
    }> {
        const response: any = await NodeAxios.patch(`/visual-engine/runs/${runId}/calibration`, patch)
        return extractObjectData(response, { success: false, run: { id: runId, name: '' }, driftCalibration: {} })
    },

    async generateCalibrationSceneGrid(
        runId: string,
        sceneId: string,
        payload: {
            recipeId?: string
            referenceStrength?: 'high' | 'low' | 'off'
            productShotUrls?: string[]
            productContext?: boolean
        } = {}
    ): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/scenes/${sceneId}/generate`, payload)
        return extractObjectData(response, response)
    },

    async generateCalibrationPackGrid(
        runId: string,
        payload: {
            sceneIds?: string[]
            recipeId?: string
            referenceStrength?: 'high' | 'low' | 'off'
            productShotUrls?: string[]
            productContext?: boolean
        } = {}
    ): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/generate-pack`, payload)
        return extractObjectData(response, response)
    },

    async submitCalibrationFeedback(runId: string, payload: any): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/feedback`, payload)
        return extractObjectData(response, response)
    },

    async recommendCalibration(runId: string, payload: { lastN?: number; sceneId?: string; gridId?: string } = {}): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/recommend`, payload)
        return extractObjectData(response, response)
    },

    async applyCalibration(runId: string, payload: any): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/apply`, payload)
        return extractObjectData(response, response)
    },

    async undoCalibration(runId: string, payload: { applyId: string }): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/undo`, payload)
        return extractObjectData(response, response)
    },

    async previewCalibration(runId: string, payload: any): Promise<any> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/preview`, payload)
        return extractObjectData(response, response)
    },

    async generateCalibrationProductBrief(
        runId: string,
        payload: { productShotUrls: string[]; additionalContext?: string } 
    ): Promise<{ success: boolean; brief?: string }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/calibration/product-brief`, payload)
        return extractObjectData(response, response)
    },

    /**
     * Save constraint preferences (disabled_constraints only — does not touch other settings)
     */
    async saveConstraintPreferences(runId: string, disabledConstraints: string[]): Promise<void> {
        await NodeAxios.patch(`/visual-engine/runs/${runId}/constraint-preferences`, {
            disabled_constraints: disabledConstraints,
        })
    },

    async getApplicationPolicy(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/application-policy`)
        return extractObjectData(response, {})
    },

    async saveApplicationPolicy(runId: string, policy: any): Promise<void> {
        await NodeAxios.patch(`/visual-engine/runs/${runId}/application-policy`, policy)
    },

    /**
     * Get generation history
     */
    async getGenerationHistory(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/api/history`)
        return extractArrayData(response)
    },

    /**
     * Get saved prompt setups
     */
    async getSavedPrompts(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/api/prompts`)
        return extractArrayData(response)
    },

    /**
     * Delete a saved prompt setup
     */
    async deletePromptSetup(runId: string, promptId: string): Promise<void> {
        await NodeAxios.delete(`/visual-engine/runs/${runId}/api/prompts/${promptId}`)
    },

    /**
     * Get usage statistics
     */
    async getUsageStats(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/api/stats`)
        return response.data !== undefined ? response.data : response
    },

    /**
     * Get all assets for library view
     */
    async getAllAssets(runId: string): Promise<any[]> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/assets`)
        return extractArrayData(response)
    },

    /**
     * Generate creative topic using AI based on PhD context
     */
    async generateCreativeTopic(
        runId: string,
        context: {
            brandName: string
            productDescription: string
            successIdentity: string
            painPoints: string
            targetProfile: string
        }
    ): Promise<{ topic: string; outline: string[] }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/generate-topic`, context)
        return response.data !== undefined ? response.data : response
    },

    /**
     * Generate strategic visual prompt from topic + outline.
     *
     * Standard mode: returns { visualPrompt, reasoning }
     *
     * Banner-ad mode (mode: 'banner-ad'): additionally returns structured
     * sections { scene, subject, tone, composition, brand_constraints,
     * text_safe_zones, exclusions } decomposed from the Step 0 prose by a
     * second lightweight AI pass. Pass optional beat context fields to make
     * the concept beat- and situation-aware.
     */
    async generateVisualPrompt(
        runId: string,
        data: {
            topic: string
            outline: string[] | string
            brandExtremity?: number
            realityMode?: string
            // Banner-ad mode
            mode?: 'banner-ad'
            beatName?: string
            beatNumber?: number
            situationLabel?: string
            targetName?: string
            campaignGoal?: string
        }
    ): Promise<{
        visualPrompt: string
        reasoning: string
        sections?: {
            scene: string
            subject: string
            tone: string
            composition: string
            brand_constraints: string
            text_safe_zones: string
            exclusions: string
        }
    }> {
        // Backend expects outline as a string (splits by |||), convert array if needed
        const payload = {
            ...data,
            outline: Array.isArray(data.outline) ? data.outline.join('|||') : data.outline,
        }
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/generate-visual-prompt`, payload)
        return response.data !== undefined ? response.data : response
    },

    /**
     * Generate creative test image from starter image + prompt
     */
    /**
     * Generate Variety Pack - 12-60 images (low quality for cost)
     * Modes: vary-settings (1 prompt × N combos) | vary-prompts (N prompts × locked settings)
     */
    async generateVarietyPack(
        runId: string,
        data: {
            prompt?: string
            count?: 12 | 24 | 30 | 36 | 48 | 60
            useReferenceImage?: boolean
            mode?: 'vary-settings' | 'vary-prompts'
            prompts?: string[]
            lockedSettings?: {
                aestheticMode: string
                aestheticColorMode: string
                brandExtremity: number
                realityMode: string
            }
            referenceImages?: Array<{ id: string; url: string; name: string; type: 'product_shot' | 'inspiration' }>
            productUsageNotes?: string
        }
    ): Promise<{
        batchId: string
        jobIds: string[]
        jobLabels: Record<string, { aesthetic: string; color: string; reality: string; extremity: string }>
        count: number
        message: string
    }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/creative-test/variety-pack`, data)
        return response.data !== undefined ? response.data : response
    },

    async generateCreativeTest(
        runId: string,
        data: {
            starterImageId?: string
            prompt: string
            profile: string
            aestheticPreset?: string
            aestheticModifiers?: string[]
            aestheticMode?: string
            aestheticSubvariants?: string[]
            aestheticColorMode?: string
            brandExtremity?: number
            realityMode?: string
            generationModel?: string
            generationQuality?: string
            referenceImages?: Array<{ id: string; url: string; name: string; type: 'product_shot' | 'inspiration' }>
            productUsageNotes?: string
        }
    ): Promise<{ jobId: string; message: string }> {
        // Try both endpoints to be safe, though the backend now has both
        try {
            const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/creative-test`, data)
            return response.data !== undefined ? response.data : response
        } catch (error: any) {
            if (error.response?.status === 404) {
                console.warn(
                    '[visualEngineService] /creative-test returned 404, trying /creative-testing/generate fallback'
                )
                const response: any = await NodeAxios.post(
                    `/visual-engine/runs/${runId}/creative-testing/generate`,
                    data
                )
                return response.data !== undefined ? response.data : response
            }
            throw error
        }
    },

    // ============================================
    // 🧬 BRAND DNA PROFILE MANAGEMENT
    // ============================================

    /**
     * Get Available Profiles and Strategic Options
     */
    async getAvailableProfiles(runId: string): Promise<{
        profiles: string[]
        verboten_list: string[]
        brand_name: string
        aesthetic_modes: Array<{ id: string; label: string; intent: string }>
        sub_variants: Array<{ id: string; label: string }>
        color_modes: Array<{ id: string; label: string; intent: string }>
        reality_modes: Array<{ id: string; label: string; description: string }>
    }> {
        const response: any = await NodeAxios.get(`/visual-engine/available-profiles?runId=${runId}`)
        return extractObjectData(response)
    },

    /**
     * Workspace-scoped API Profiles (Visual Engine)
     */
    async getWorkspaceApiProfiles(): Promise<{
        default_profile_id: string | null
        profiles: Array<{
            id: string
            name: string
            is_active: boolean
            imageControls: Record<string, any>
        }>
    }> {
        const response: any = await NodeAxios.get(`/visual-engine/workspace-api-profiles`)
        return extractObjectData(response, { default_profile_id: null, profiles: [] })
    },

    async createWorkspaceApiProfile(payload: { name?: string; baseProfileId?: string }) {
        const response: any = await NodeAxios.post(`/visual-engine/workspace-api-profiles`, payload)
        return extractObjectData(response)
    },

    async updateWorkspaceApiProfile(profileId: string, payload: { name?: string; is_active?: boolean; imageControls?: any }) {
        const response: any = await NodeAxios.patch(`/visual-engine/workspace-api-profiles/${profileId}`, payload)
        return extractObjectData(response)
    },

    async duplicateWorkspaceApiProfile(profileId: string, payload: { name?: string } = {}) {
        const response: any = await NodeAxios.post(`/visual-engine/workspace-api-profiles/${profileId}/duplicate`, payload)
        return extractObjectData(response)
    },

    async setDefaultWorkspaceApiProfile(profileId: string) {
        const response: any = await NodeAxios.post(`/visual-engine/workspace-api-profiles/default`, { profileId })
        return extractObjectData(response)
    },

    /**
     * Get active DNA profile for a Visual Identity Run
     */
    async getActiveDNAProfile(runId: string): Promise<any> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/dna-profile`)
        return extractObjectData(response)
    },

    /**
     * Get DNA profile version history for a Visual Identity Run
     */
    async getDNAProfileHistory(runId: string): Promise<{
        profiles: Array<{
            profile_id: string
            profile_version: string
            created_at: string
            is_active: boolean
            dna_summary: any
        }>
        total: number
        active_version: string
    }> {
        const response: any = await NodeAxios.get(`/visual-engine/runs/${runId}/dna-profiles/history`)
        return extractObjectData(response, { profiles: [], total: 0, active_version: null })
    },

    /**
     * Reset DNA compilation status to allow retry
     */
    async resetDNACompilationStatus(runId: string): Promise<{ success: boolean; message: string }> {
        const response: any = await NodeAxios.post(`/visual-engine/runs/${runId}/dna-profile/reset`)
        return extractObjectData(response, { success: false, message: '' })
    },

    /**
     * Get specific DNA profile by ID
     */
    async getDNAProfileById(profileId: string): Promise<any> {
        // Add cache-busting timestamp to prevent stale data
        const cacheBuster = Date.now()
        const response: any = await NodeAxios.get(`/visual-engine/dna-profiles/${profileId}?t=${cacheBuster}`)
        return extractObjectData(response)
    },

    /**
     * Activate a specific DNA profile version
     */
    async activateDNAProfile(
        profileId: string
    ): Promise<{ profile_id: string; profile_version: string; is_active: boolean }> {
        const response: any = await NodeAxios.post(`/visual-engine/dna-profiles/${profileId}/activate`)
        return extractObjectData(response)
    },

    /**
     * Compare two DNA profile versions
     */
    async compareDNAProfiles(
        profile1: string,
        profile2: string
    ): Promise<{
        profile1: any
        profile2: any
        differences: any
        similarity_percentage: number
        total_differences: number
    }> {
        const response: any = await NodeAxios.get(
            `/visual-engine/dna-profiles/compare?profile1=${profile1}&profile2=${profile2}`
        )
        return extractObjectData(response)
    },

    /**
     * Generate Visual Prompt for Content Creation (with optional PhD settings)
     * POST /api/visual-engine/generate-content-prompt
     */
    async generateContentPrompt(data: {
        // Campaign/Asset Context
        campaignId: string
        assetId?: string
        beatNumber?: number

        // Topic/Content Context
        topicTitle?: string
        channelType: string
        generatedContent?: string // 🆕 The actual post content for better visual alignment
        existingPrompt?: string
        additionalContext?: string

        // PhD Settings (optional - when PhD is active)
        visualRunId?: string
        profile?: string
        aestheticMode?: string
        colorMode?: string
        realityMode?: string
        aestheticLens?: string
        brandExtremity?: number
        subvariants?: string[]
        locks?: Record<string, boolean>
    }): Promise<{
        success: boolean
        jobId?: string
        visualPrompt?: string // If synchronous
        message?: string
    }> {
        const response: any = await NodeAxios.post('/visual-engine/generate-content-prompt', data)
        return response.data !== undefined ? response.data : response
    },

    /**
     * Generate Strategic Prompt via PhD API (External/Internal Integration)
     */
    async generateStrategicPrompt(
        runId: string,
        payload: {
            topic: string
            outline: string | string[]
            profile?: string
            aestheticMode?: string
            aestheticSubvariants?: string[]
            aestheticColorMode?: string
            aestheticPreset?: string
            aestheticModifiers?: string[]
            brandExtremity?: number
            realityMode?: string
            generationModel?: string
            generationQuality?: string
            aspectRatio?: string // 🆕 Aspect ratio for image generation (e.g., '1:1', '16:9', '4:5')
            starterImageUrl?: string
            starterImageStyleWeight?: number
            generateImage?: boolean
            callbackUrl?: string
            assetId?: string
            metadata?: any
            verbotenList?: string[]
        }
    ): Promise<{
        success: boolean
        data: {
            prompt: string
            negative_prompt?: string
            image_queued?: boolean
            visual_concept_reasoning?: string
            notifications?: Array<{ type: string; message: string }>
            metadata?: any
        }
    }> {
        const response: any = await NodeAxios.post(`/visual-engine/prompt`, {
            ...payload,
            runId,
            outline: Array.isArray(payload.outline) ? payload.outline.join('|||') : payload.outline,
        })
        return response
    },
}
